import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMic, FiTarget, FiEye, FiShield, FiCheckCircle } = FiIcons;

const AIInterviewSection = () => {
  const aiFeatures = [
    {
      icon: FiMic,
      title: 'Transcript Generation',
      description: 'Accurate speech-to-text with context understanding'
    },
    {
      icon: FiTarget,
      title: 'Keyword/Skill Match',
      description: 'Automatic identification of relevant skills and keywords'
    },
    {
      icon: FiEye,
      title: 'Soft-Skill Inference',
      description: 'AI analysis of communication style and confidence'
    },
    {
      icon: FiShield,
      title: 'Proxy Detection',
      description: 'Advanced verification to ensure interview authenticity'
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-slate-900 mb-6">
            AI Interview Intelligence & Safety
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Advanced AI analysis provides deep insights while maintaining fairness and transparency
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Left Column - Features */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {aiFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-elegant"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Right Column - AI Analysis Mockup */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg text-slate-900">AI Analysis Results</h3>
                <div className="flex items-center space-x-2 text-green-600">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
                  <span className="text-sm font-medium">Verified</span>
                </div>
              </div>

              {/* Candidate Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-indigo-600">JD</span>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">John Doe</div>
                    <div className="text-sm text-slate-500">Senior Developer Position</div>
                  </div>
                </div>
              </div>

              {/* Skill Scores */}
              <div className="space-y-4 mb-6">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">Technical Skills</span>
                    <span className="text-sm font-medium text-green-600">92%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '92%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">Communication</span>
                    <span className="text-sm font-medium text-blue-600">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '85%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">Problem Solving</span>
                    <span className="text-sm font-medium text-purple-600">88%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{width: '88%'}}></div>
                  </div>
                </div>
              </div>

              {/* Key Insights */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">Key Insights</h4>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Strong React and Node.js experience</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Excellent communication clarity</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span>Could improve on system design</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trust Box */}
        <motion.div
          className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <SafeIcon icon={FiShield} className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h3 className="font-semibold text-xl mb-2">Transparency & Compliance</h3>
          <p className="text-indigo-100 max-w-2xl mx-auto">
            All AI analysis is transparent and auditable. Human decisions are always final, 
            ensuring fair and compliant hiring practices.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AIInterviewSection;